import BaseService from "./BaseService";
import { IInstallation } from "../core/contracts/IInstallation";
// import { mockRequest, makeCustomerWithID /* , makeInstallationWithID*/ } from "./MockService";
import { SessionCache } from "../helpers/cache";
import { isArrayValid, isNumberValid } from "../helpers/utility";
import CustomerModel from "../core/models/Customer";
import sortBy from "lodash/sortBy";
import get from "lodash/get";
import User from "../core/models/User";
import InstallationGroup from "../core/models/InstallationGroup";
import { IEnumerable } from "../core/models/ModelHelpers";
import AllInstallationsFilter from "../core/models/AllInstallationsFilter";
import { join, uniq, uniqBy } from "lodash";
import APIInstallationFilter from "../core/contracts/APIInstallationFilter";
import { APIAllInstallationsVendor } from "../core/contracts/AllInstallationsFilter";
import { EVENT_ENDPOINT_TIMEOUT } from "../Events";
import { Debugger } from "inspector";
import { installationOrderType } from '../core/models/OrderType';

export default class InstallationService extends BaseService {
  async fetchAll(customer_id: string): Promise<CustomerModel> {
    try {
      const storage = new SessionCache();
      const cachedClients: CustomerModel[] | null = storage.get("customers");
      if (isArrayValid(cachedClients)) {
        const cachedInstallations = cachedClients?.find((x) => x.customer_id === customer_id);

        if (cachedInstallations) {
          // eslint-disable-next-line no-console
          console.log(
            "InstallationService -> fetchAll -> Instalação recuperada do cache com sucesso!"
          );
          return cachedInstallations;
        }
      } else {
        const cachedUser: User | null = storage.get("user");
        const cachedCustomer: InstallationGroup | undefined = get(
          cachedUser?.installationGroup?.filter((x) => x.id === customer_id),
          "[0]"
        );
        const cachedInstallationIds =
          (cachedCustomer?.installationList
            ?.map((x) => x.installationId)
            .filter((x) => x) as IEnumerable[]) ?? [];

        if (isArrayValid(cachedInstallationIds)) {
          try {
            const installationsFromAPI: IInstallation[] = await new InstallationService().fetchMultiple(
              cachedInstallationIds
            );
            return {
              customer_id: cachedCustomer?.id.toString() ?? "",
              customer_name: cachedCustomer?.name ?? "",
              installations: isArrayValid(installationsFromAPI)
                ? sortBy(installationsFromAPI, "product_description")
                : [],
            };
          } catch (error) {
            // eslint-disable-next-line no-console
            console.log("InstallationService -> fetchAll → error", error);
            return new Promise((_, rej) => rej(error));
          }
        } else {
          return new Promise((_, rej) => rej("unable to resolve installations."));
        }
      }

      return new Promise((_, rej) => rej("unable to resolve installations."));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("InstallationService -> fetchAll -> error", error);
      return new Promise((_, rej) => rej(error));
    }
  }

  async fetch(installation_id: IEnumerable): Promise<IInstallation> {
    try {
      const APIResult: { installations: IInstallation[] } = await new BaseService().post<
        { installations: IInstallation[] },
        { installation_id: IEnumerable }[]
      >(`/installation/`, [{ installation_id: installation_id }]);

      if (isArrayValid(APIResult.installations)) {
        return APIResult.installations[0];
      }
      return new Promise((_, rej) => rej("unable to complete fetch"));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("InstallationService -> fetch -> error", error);
      return new Promise((_, rej) => rej(error));
    }
  }

  async fetchMultiple(installation_ids: IEnumerable[]): Promise<IInstallation[]> {
    if (!isArrayValid(installation_ids)) {
      return Promise.resolve([]);
    }

    const params = uniq(installation_ids).map((x) => {
      return { installation_id: x };
    });

    try {
      const APIResponse = await new BaseService().post<
        { installations: IInstallation[] },
        { installation_id: IEnumerable }[]
      >(`/installation/`, params);

      return uniqBy(APIResponse?.installations, "installation_id");
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("InstallationService -> fetch -> error", error);
      return new Promise((_, rej) => rej(error));
    }
  }

  async search(searchParam: string) {
    try {
      return uniqBy(
        await new BaseService().get<IInstallation[]>(`/customer/?filter=${searchParam}`),
        "installation_id"
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("InstallationService -> search -> error", error);
      return new Promise((_, rej) => rej(error));
    }
  }

  async filter(filters?: AllInstallationsFilter) {
    try {
      let filterId = "",
        filterName = "";

      if (filters?.installation_name_id) {
        if (isNumberValid(filters.installation_name_id)) {
          filterId = filters.installation_name_id;
        } else {
          filterName = filters.installation_name_id.toUpperCase();
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const params: Record<string, string>[] = [];

      for (const key in filters) {
        if (key !== "installation_name_id" && (filters as Record<string, string>)[key]) {
          params.push({ [key]: (filters as Record<string, string>)[key] });
        }
      }

      if (filterId) params.push({ installation_id: filterId });

      if (filterName) params.push({ installation_name: filterName });

      const installationsIds = isArrayValid(params)
        ? await new BaseService(false).post<
            APIInstallationFilter,
            // eslint-disable-next-line @typescript-eslint/ban-types
            undefined | Record<string, string>[]
          >(`/filter`, params)
        : await new BaseService(false).post<
            APIInstallationFilter,
            // eslint-disable-next-line @typescript-eslint/ban-types
            {}
          >(`/filter`);

      if (isArrayValid(installationsIds?.installations)) {
        return installationsIds?.installations?.map((x) => x.installation_id) ?? [];
      }

      return [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        "🚀 ~ file: InstallationService.ts ~ line 127 ~ InstallationService ~ filter ~ error",
        error
      );
      return Promise.reject(error);
    }
  }

  async filterNew(filters?: AllInstallationsFilter) {
    try {
      const storage = new SessionCache();
      const whereFilterMounted: Record<string, any>[] = [];

      if (filters?.installation_name_id) {
        if (isNumberValid(filters.installation_name_id)) {
          whereFilterMounted.push({
            field: "installation_id",
            operator: "LIKE",
            value: "%" + filters.installation_name_id + "%",
          });
        } else {
          whereFilterMounted.push({
            field: "name",
            operator: "LIKE",
            value: "%" + filters.installation_name_id.toUpperCase() + "%",
          });
        }
      }

      if (filters?.installation_ids && filters?.installation_ids?.length > 0) {
        whereFilterMounted.push({
          field: "installation_id",
          operator: "IN",
          values: filters?.installation_ids?.map((val) => {
            return { value: val };
          }),
        });
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const auxFilters: Record<string, string[]> = {};

      for (const key in filters) {
        if (
          key !== "installation_name_id" &&
          key !== "installation_ids" &&
          key !== "offset" &&
          key !== "order_installation" &&
          (filters as Record<string, string>)[key]
        ) {
          auxFilters[key] = (filters as Record<string, string[]>)[key];
        }
      }

      const fieldTypeMapped: Record<string, string> = {
        installation_product: "product_code",
        installation_vendor: "rtu_vendor",
        installation_uf: "uf",
        installation_ldc: "responsible_ldc",
        installation_market: "market_segmentation",
        installation_alert: "installation_level_status",
        installation_trip_status: "trip_status",
        installation_orderType : "order_installation",
      };

      let params: Record<string, any> = {
        offset: filters?.offset || 0,
        page_size: filters?.page_size || 10,
      };

      const orderFilterMounted: Record<string, any>[] = [];

      if (filters?.order) {
        const key = Object.keys(filters.order)[0];
        orderFilterMounted.push({
          field: key,
          value: filters?.order[key],
        });
      }

      Object.keys(auxFilters).forEach((value: string, index: number) => {
        const filterValues = (auxFilters[value] as unknown) as string[];

        if (filterValues && isArrayValid(filterValues)) {
          const elements = filterValues?.filter((e) => e != "all");
          if (elements.length) {
            whereFilterMounted.push({
              field: fieldTypeMapped[value],
              operator: "IN",
              values: elements.map((val) => {
                return { value: val };
              }),
            });
          }
        }
      });

      if (!orderFilterMounted.length) {
        orderFilterMounted.push({
          field: "installation_id",
          value: "asc",
        });
      }

      if (filters?.installation_has_rtu) {
        let possibleVendors: APIAllInstallationsVendor[] | null = storage.get("vendor");
        if (!possibleVendors?.length) {
          possibleVendors = await new BaseService(false).get("/vendor");
        }
        if (!possibleVendors?.length) {
          window?.Hubx?.notify(EVENT_ENDPOINT_TIMEOUT, true);
          return [];
        }
        const vendors = possibleVendors?.map((x) => x.rtu_vendor ?? "") ?? [];
        whereFilterMounted.push({
          field: "rtu_vendor",
          operator: "IN",
          values: vendors
            ? vendors.map((val) => {
                return { value: val };
              })
            : [],
        });
      }
      if (filters?.installation_has_rtu) {
        whereFilterMounted.push({
          field: "has_rtu",
          operator: "=",
          value: filters?.installation_has_rtu,
        });
      }

      params = {
        ...params,
        where: whereFilterMounted.length > 0 ? whereFilterMounted : undefined,
        order: orderFilterMounted,
      };

      const installationsIds = await new BaseService(false).post<
        APIInstallationFilter,
        // eslint-disable-next-line @typescript-eslint/ban-types
        undefined | Record<string, any>
      >(`/filtered_installations`, params);

      if (filters?.return_ids == undefined) {
        if (isArrayValid(installationsIds?.installations)) {
          return installationsIds?.installations?.map((x) => x.installation_id) ?? [];
        }
      }
      return installationsIds?.installations;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        "🚀 ~ file: InstallationService.ts ~ line 127 ~ InstallationService ~ filter ~ error",
        error
      );
      return Promise.reject(error);
    }
  }
}
